import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AdjustmentList } from "@/components/permessi/AdjustmentList";

function KindToggle({ value, onChange }) {
  const opt = (k, label) => (
    <button type="button" onClick={() => onChange(k)} data-testid={`adjust-kind-${k}`}
      className={`flex-1 h-9 rounded-full border text-xs font-medium transition-colors duration-150 ${value === k ? "bg-[#1C352D] text-white border-[#1C352D]" : "bg-white text-stone-600 border-stone-300"}`}>
      {label}
    </button>
  );
  return <div className="flex gap-2">{opt("riduzione", "Togli ore")}{opt("aggiunta", "Aggiungi ore")}</div>;
}

export function LeaveAdjustDialog({ row, onClose }) {
  const qc = useQueryClient();
  const [kind, setKind] = useState("riduzione");
  const [hours, setHours] = useState("");
  const [reason, setReason] = useState("");
  const sid = row?.student_id;

  const { data: history = [] } = useQuery({
    queryKey: ["leave-adjustments", sid],
    queryFn: async () => (await api.get(`/leave-quotas/${sid}/adjustments`)).data,
    enabled: !!sid,
  });
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["leave-adjustments", sid] });
    qc.invalidateQueries({ queryKey: ["leave-quotas"] });
  };
  const add = useMutation({
    mutationFn: async () => (await api.post(`/leave-quotas/${sid}/adjustments`, { kind, hours: Number(hours), reason })).data,
    onSuccess: () => { toast.success("Rettifica registrata"); setHours(""); setReason(""); refresh(); },
    onError: (e) => toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || "Errore"),
  });
  const cancel = useMutation({
    mutationFn: async (id) => (await api.post(`/leave-quotas/adjustments/${id}/cancel`)).data,
    onSuccess: () => { toast.success("Rettifica annullata"); refresh(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Errore"),
  });

  const h = Number(hours) || 0;
  const after = row ? Math.round((row.available_hours + (kind === "riduzione" ? -h : h)) * 100) / 100 : 0;
  const valid = h > 0 && reason.trim().length >= 3;

  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="leave-adjust-dialog">
        <DialogHeader><DialogTitle>Rettifica monte ore · {row?.student_name}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <KindToggle value={kind} onChange={setKind} />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Ore</Label>
              <Input type="number" min="0.5" step="0.5" value={hours} onChange={(e) => setHours(e.target.value)} className="mt-1.5" data-testid="adjust-hours-input" />
            </div>
            <div className="text-sm pt-6">
              <div className="text-stone-500 text-xs">Residuo: {row?.available_hours}h →</div>
              <div className={`font-semibold ${after < 0 ? "text-red-600" : "text-[#1C352D]"}`} data-testid="adjust-after">{after}h</div>
            </div>
          </div>
          <div>
            <Label>Motivo (visibile allo studente)</Label>
            <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Es. Permessi già goduti a settembre, prima dell'app" className="mt-1.5" data-testid="adjust-reason-input" />
          </div>
          <Button onClick={() => add.mutate()} disabled={!valid || add.isPending} className="w-full rounded-full bg-[#1C352D] hover:bg-[#264A3E]" data-testid="adjust-save-btn">
            {add.isPending ? "Salvataggio…" : "Registra rettifica"}
          </Button>
          <div className="pt-2 border-t border-stone-100">
            <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-2">Storico rettifiche</div>
            <AdjustmentList items={history} onCancel={(a) => { if (window.confirm("Annullare questa rettifica? Le ore verranno ripristinate.")) cancel.mutate(a.id); }} />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
